import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { select, Store } from '@ngrx/store'
import { environment } from 'src/environments/environment'
import { AppState } from '../ngrx-store/app.state'
import { CorpMemberState } from '../ngrx-store/app.state'
import { io, Socket } from 'socket.io-client'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { share } from 'rxjs/operators'
import { IOEventName, IOEventRoutes } from '../utils/types'
import { ChatMessage, ChatSendErrorCode, OutgoingChatMessage } from '../ngrx-store/app.state'
import { chatSendErrorCode } from '../utils/chat-outbox.util'

/**
 * The backend's ack for a saved CHAT_MESSAGE. Backends from before the chat outbox send only
 * `status` and `id` - the room broadcast then shows the saved message instead of `chat`.
 */
export type ChatMessageDelivered = { status: 'DELIVERED'; id: number; room?: string; client_msg_id?: string | null; chat?: ChatMessage }
/** The backend's ack for a CHAT_MESSAGE. `error` is for logs only - the frontend's copy comes from `code`. */
export type ChatMessageAck = ChatMessageDelivered | { status: 'FAILED'; code?: string; error?: string; client_msg_id?: string | null }

/** Why sendChatMessage() gave up on a message. */
export class ChatSendError extends Error {
    constructor(public readonly code: ChatSendErrorCode) {
        super(`chat message not sent: ${code}`)
    }
}

@Injectable({
    providedIn: 'root',
})
export class SocketIoChatNamespaceService {
    corpMember!: CorpMemberState

    socket: Socket

    // One multicasted Observable per event name, shared across every consumer — see
    // SocketIoService.onEvent() for the full rationale (fixes the double-emission bug this
    // service used to have, since every subscriber used to attach its own raw socket.on()).
    private eventStreams = new Map<IOEventName, Observable<any>>()

    private connectedSubject = new BehaviorSubject<boolean>(false)
    /** Reflects the actual transport state (not browser online/offline) - drives a
     *  connectivity indicator in the chat UI. */
    public readonly connected$: Observable<boolean> = this.connectedSubject.asObservable()

    private hasConnectedBefore = false
    private reconnectedSubject = new Subject<void>()
    /** Fires on every connect *after* the first one for this socket instance - i.e. a real
     *  reconnect following a drop, not the initial app-boot connection (which the messages
     *  page already loads fresh data for via its resolver). Consumers can use this to resync
     *  anything that might have been missed while disconnected. */
    public readonly reconnected$: Observable<void> = this.reconnectedSubject.asObservable()

    constructor(public http: HttpClient, public store: Store<AppState>) {
        this.store.pipe(select('corper')).subscribe({
            next: (value) => {
                this.corpMember = value
            },
        })

        // autoConnect: false — the socket object exists immediately (safe for effects to attach
        // .on() listeners to at app bootstrap, before login), but no network connection opens
        // until connect() is explicitly called (see CallerService.connectSockets()).
        this.socket = io(environment.basehost + IOEventRoutes.CHAT, {
            transports: ['websocket', 'polling'],
            autoConnect: false,
        })

        this.socket.on(IOEventName.CONNECT, () => {
            console.log('Connected to Socket.IO/chat')
            this.connectedSubject.next(true)
            if (this.hasConnectedBefore) {
                this.reconnectedSubject.next()
            }
            this.hasConnectedBefore = true
        })

        this.socket.on(IOEventName.CONNECT_ERROR, (error) => {
            console.log(error.message)
            // The backend rejects the handshake outright (see ioChat.use() in
            // sockets/routes.js) when the token is missing/invalid rather than a transport
            // problem. Socket.IO's default reconnection would otherwise retry forever with
            // that same doomed token — spamming the backend and leaving connected$ stuck at
            // false indefinitely, with no visible sign it's an auth problem rather than a
            // network one. Stop retrying; connect() reopens the transport (with a fresh
            // token) the next time the app dispatches setCorpMemberProfileData.
            if (error.message?.startsWith('Unauthorized')) {
                this.socket.disconnect()
            }
        })

        this.socket.on(IOEventName.DISCONNECT, () => {
            console.log('Disconnected from Socket.IO/chat')
            this.connectedSubject.next(false)
        })
    }

    /**
     * Idempotent — safe to call on every login/app-boot; a no-op if already connected.
     * Refreshes the query params and auth token to the current corpMember identity before
     * connecting, so a reconnect after a different user logs in in the same tab doesn't carry
     * stale identity.
     */
    public connect(): void {
        if (this.socket.connected) return

        // No usable session yet - the backend's ioChat.use() would reject the handshake
        // outright anyway. Bail out instead of opening a transport that can never
        // authenticate; a later call (e.g. once login sets a token) will retry properly.
        const token = this.getAuthToken()
        if (!token) return

        this.socket.io.opts.query = {
            state_code: this.corpMember?.state_code,
            // Chats now identify participants by slug_id — keep sending the legacy numeric id
            // too so the backend can still resolve this connection during the gap between a
            // backend restart and full frontend rollout.
            id: this.corpMember?.id,
            slug_id: this.corpMember?.slug_id,
        }
        // The /chat namespace verifies this against the session JWT during the handshake (see
        // ioChat.use() on the backend) rather than trusting the query params above for identity -
        // `socket.auth` (distinct from `socket.io.opts.query`) is resent on every (re)connect
        // attempt, so a token refreshed after login is always picked up.
        this.socket.auth = { token }
        this.socket.connect()
    }

    /** Mirrors BaseService.SESSION_STORAGE_DATA_KEY's key format without importing BaseService
     *  itself here, which would pull in its dialog-component dependencies for no reason. */
    private getAuthToken(): string | null {
        return sessionStorage.getItem('_online_' + environment.env)
    }

    public onEvent<T>(event: IOEventName): Observable<T> {
        if (!this.eventStreams.has(event)) {
            const stream$ = new Observable<T>((observer) => {
                const handler = (data: T) => observer.next(data)
                this.socket.on(event, handler)
                return () => this.socket.off(event, handler)
            }).pipe(share())
            this.eventStreams.set(event, stream$)
        }
        return this.eventStreams.get(event)!
    }

    /** True while the transport is up - a message sent now goes straight out. */
    public get isConnected(): boolean {
        return this.socket.connected
    }

    /**
     * How long each attempt waits for its ack before resending. Each waits longer than the last:
     * the ack only comes once the backend has saved the message (several DB round-trips), so a
     * slow backend gets time to answer instead of a quick burst of resends.
     */
    private static readonly SEND_ATTEMPT_TIMEOUTS_MS = [5000, 10000, 15000]

    /**
     * Sends a chat message; emits the backend's ack once it has saved it, then completes.
     * A missed ack is resent (safe: the backend won't save the same `client_msg_id` twice), once
     * per SEND_ATTEMPT_TIMEOUTS_MS. While the socket is down each attempt waits in socket.io's
     * send buffer, and its timeout takes it back out - so a message given up on here is never
     * sent later. Errors with a ChatSendError: straight away with the backend's code when it
     * turns the message down (a resend would only be turned down again), or TIMEOUT once every
     * attempt has timed out. Nothing is sent until it's subscribed to.
     */
    public sendChatMessage(data: OutgoingChatMessage): Observable<ChatMessageDelivered> {
        return new Observable<ChatMessageDelivered>((subscriber) => {
            // Goes through connect() so a reconnect sends the current auth token.
            if (this.socket.disconnected) {
                console.log('io was disconnected')
                this.connect()
            }

            const attempt = (index: number) => {
                this.socket
                    .timeout(SocketIoChatNamespaceService.SEND_ATTEMPT_TIMEOUTS_MS[index])
                    .emit(IOEventName.CHAT_MESSAGE, data, (err: unknown, v: ChatMessageAck) => {
                        if (subscriber.closed) return
                        if (err) {
                            console.log('chat message ack timed out, attempt', index + 1)
                            if (index + 1 < SocketIoChatNamespaceService.SEND_ATTEMPT_TIMEOUTS_MS.length) {
                                attempt(index + 1)
                            } else {
                                subscriber.error(new ChatSendError('TIMEOUT'))
                            }
                            return
                        }
                        if (v?.status === 'FAILED') {
                            console.log('server turned the chat message down', v.code)
                            subscriber.error(new ChatSendError(chatSendErrorCode(v.code)))
                            return
                        }
                        subscriber.next(v)
                        subscriber.complete()
                    })
            }
            attempt(0)
        })
    }

    /**
     * Closes the transport only — deliberately does NOT call removeAllListeners(). Listeners
     * registered via onEvent() (e.g. SocketEffects) are meant to live for the app's session and
     * must still be attached when connect() reopens the transport on the next login.
     */
    public destroy() {
        if (this.socket) {
            this.socket.disconnect()
        }
    }
}
